import React from "react";
import { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  useEditableControls,
  ButtonGroup,
  IconButton,
  Editable,
  EditablePreview,
  EditableInput,
  Flex,
  Text,
  Image,
  Box,
  useToast,
} from "@chakra-ui/react";

import { FaCheck } from "react-icons/fa6";
import { MdClose, MdEdit } from "react-icons/md";
import GradientBorder from "components/GradientBorder/GradientBorder";
import useApiReq from "variables/apireq";

function SubmissionModal({
  isOpen,
  onClose,
  link = "https://example.comfdsfdjfkljdskljfhjkdsafhkdffdsfdsfddone",
  uuid,
  publishedImages,
}) {
  // Custom controls for the Editable component
  function EditableControls() {
    const {
      isEditing,
      getSubmitButtonProps,
      getCancelButtonProps,
      getEditButtonProps,
    } = useEditableControls();

    return isEditing ? (
      <ButtonGroup justifyContent="center" size="sm">
        <IconButton
          colorScheme="teal"
          icon={<FaCheck />}
          {...getSubmitButtonProps()}
        />
        <IconButton
          colorScheme="teal"
          icon={<MdClose />}
          {...getCancelButtonProps()}
        />
      </ButtonGroup>
    ) : (
      <Flex justifyContent="center">
        <IconButton
          colorScheme="teal"
          size="sm"
          icon={<MdEdit />}
          {...getEditButtonProps()}
        />
      </Flex>
    );
  }

  const toast = useToast({ position: "top" });

  const titleColor = "white";
  const textColor = "gray.400";

  const [selectedbanner, setSelectedBanner] = useState([]);
  const [formData, setFormData] = useState({
    publishedImages: [],
    link: link,
  });

  useEffect(() => {
    if (publishedImages) {
      const imageArray = JSON.parse(publishedImages);
      setSelectedBanner(imageArray);
      setFormData((prev) => ({
        ...prev,
        publishedImages: imageArray,
      }));
    }

    if (link) {
      setFormData((prev) => ({
        ...prev,
        link: link,
      }));
    }
  }, [publishedImages, link]);

  const fetchApprovedCampaign = async (data) => {
    try {
      const res = await useApiReq({
        method: "POST",
        url: "campaign/approve/",
        data: data,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (res.error) {
        toast({
          title: "ALERT",
          description: "Error occurred while submitting the form",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      if (res.response) {
        toast({
          title: "Success",
          description: "Form submitted successfully",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        onClose();
      }
    } catch (error) {
      toast({
        title: "ALERT",
        description: "Error occurred while submitting the form",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (name === "publishedImages") {
      const newFiles = Array.from(files);

      if (newFiles.length > 5) {
        toast({
          title: "ALERT",
          description: "You can only upload up to 5 files",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      const fileReaders = [];
      const previews = [];

      newFiles.forEach((file) => {
        if (file.size > 1 * 1024 * 1024) {
          toast({
            title: "ALERT",
            description: `File "${file.name}" is too large`,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          return;
        }

        const reader = new FileReader();
        fileReaders.push(
          new Promise((resolve) => {
            reader.onloadend = () => resolve({ file, preview: reader.result });
          })
        );
        reader.readAsDataURL(file);
      });

      Promise.all(fileReaders).then((results) => {
        setSelectedBanner((prev) => [
          ...prev,
          ...results.map((res) => res.preview),
        ]);

        setFormData((prev) => ({
          ...prev,
          publishedImages: [
            ...prev.publishedImages,
            ...results.map((res) => res.file),
          ],
        }));
      });

      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const removeBannerItem = (indexToRemove) => {
    setSelectedBanner((prev) => prev.filter((_, i) => i !== indexToRemove));
    setFormData((prev) => ({
      ...prev,
      publishedImages: prev.publishedImages.filter(
        (_, i) => i !== indexToRemove
      ),
    }));
  };

  const handleEmptyFields = () => {
    setFormData((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([_, value]) => value && value.length > 0)
      )
    );
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    handleEmptyFields();
    // Perform validation
    if (!formData.link) {
      toast({
        title: "ALERT",
        description: "Please enter a link",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    if (formData.publishedImages.length === 0) {
      toast({
        title: "ALERT",
        description: "Please upload at least one image",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const data = {
      uuid: uuid,
      links: formData.link,
      images: JSON.stringify(selectedbanner),
      approval: "Approved",
    };

    console.log("Form Data:", data);

    // Add your submission logic here

    // For example, you can send the data to your server using fetch or axios
    fetchApprovedCampaign(data);
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="2xl">
        <ModalOverlay />
        <ModalContent
          p="20px"
          bg={{
            base: "rgb(19,21,56)",
          }}
        >
          <ModalHeader>
            <Text fontSize="lg" color="#fff" fontWeight="bold">
              {"Submission Form"}
            </Text>
          </ModalHeader>
          <ModalCloseButton color="#fff" />
          <ModalBody>
            <GradientBorder
              p="2px"
              w="100%"
              me={{
                base: "none",
                lg: "30px",
                xl: "none",
              }}
            >
              <Flex
                background="transparent"
                color="#fff"
                borderRadius="30px"
                direction="column"
                p="24px"
                minW={{
                  base: "unset",
                  md: "430px",
                  xl: "450px",
                }}
                w="100%"
                mx={{ base: "0px" }}
                bg={{
                  base: "rgb(19,21,56)",
                }}
              >
                <form>
                  <FormControl mt={4}>
                    <FormLabel
                      color={titleColor}
                      ms="4px"
                      fontSize="lg"
                      fontWeight="normal"
                    >
                      Link
                    </FormLabel>
                    <Editable
                      defaultValue={link}
                      fontSize="lg"
                      isPreviewFocusable={false}
                      value={formData.link}
                      onChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          link: value,
                        }))
                      }
                      style={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <EditablePreview
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        maxWidth="350px"
                      />
                      {/* Here is the custom input */}
                      <Input as={EditableInput} />
                      <EditableControls />
                    </Editable>

                    <Flex
                      direction="column"
                      justifyContent={"flex-start"}
                      w={"100%"}
                      mb="14px"
                      mt="14px"
                    >
                      <FormLabel
                        color={titleColor}
                        ms="4px"
                        fontSize="lg"
                        fontWeight="normal"
                      >
                        Images
                      </FormLabel>

                      <Input
                        color={titleColor}
                        bg={{
                          base: "transparent",
                        }}
                        maxBlockSize={"200px"}
                        border="transparent"
                        borderRadius="20px"
                        fontSize="sm"
                        size="lg"
                        w="auto"
                        type="file"
                        multiple
                        pt={1}
                        sx={{
                          "::file-selector-button": {
                            background: "rgb(30,33,80)",
                            color: "white",
                            padding: "10px 20px",
                            borderRadius: "10px",
                            border: "none",
                            cursor: "pointer",
                            fontSize: "14px",
                          },
                          "::file-selector-button:hover": {
                            background: "rgb(40,45,100)",
                            cursor: "pointer",
                          },
                        }}
                        placeholder="Upload Images"
                        name="publishedImages"
                        id="bannerInput"
                        // value={formData.banner}
                        onChange={handleChange}
                        style={{ display: "none" }}
                        disabled={selectedbanner.length >= 5}
                      />

                      <Flex
                        direction="row"
                        justifyContent={"flex-start"}
                        align="center"
                        w={"100%"}
                        mb="8px"
                        mt="4px"
                        mr="12px"
                        gap={2}
                      >
                        <Button
                          onClick={() =>
                            document.getElementById("bannerInput").click()
                          }
                          bg="rgb(30,33,80)"
                          color="white"
                          px="20px"
                          py="10px"
                          borderRadius="10px"
                          fontSize="14px"
                          _hover={{ bg: "rgb(40,45,100)" }}
                          disabled={selectedbanner.length >= 5}
                        >
                          Upload Images
                        </Button>

                        <Text
                          color={textColor}
                          fontSize="sm"
                          fontWeight="normal"
                          ms="4px"
                        >
                          {selectedbanner.length > 0
                            ? selectedbanner.length
                            : "No"}{" "}
                          Image Selected
                        </Text>
                      </Flex>
                    </Flex>
                    <Flex wrap="wrap" justifyContent="flex-end" gap={4}>
                      {selectedbanner.map((src, index) => (
                        <Box
                          w="100%"
                          h="200px"
                          key={index}
                          boxSize="100px"
                          overflow={"hidden"}
                          borderRadius="10px"
                          display={"flex"}
                          justifyContent={"center"}
                          alignItems={"center"}
                          position="relative"
                          mb={2}
                        >
                          <Image
                            src={src}
                            alt={`image-${index}`}
                            w="auto"
                            h="200px"
                            boxSize="100%"
                            objectFit="contain"
                            borderRadius="10px"
                          />
                          <Button
                            size="xs"
                            position="absolute"
                            top="2px"
                            right="2px"
                            colorScheme="red"
                            borderRadius="full"
                            onClick={() => removeBannerItem(index)}
                          >
                            X
                          </Button>
                        </Box>
                      ))}
                    </Flex>
                  </FormControl>
                </form>
              </Flex>
            </GradientBorder>

            {/* <FormControl>
              <FormLabel>First name</FormLabel>
              <Input placeholder="First name" />
            </FormControl> */}
          </ModalBody>

          <ModalFooter>
            <Button onClick={handleSubmit} colorScheme="blue" mr={3}>
              Save
            </Button>
            <Button onClick={onClose}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default SubmissionModal;
